import { apiFetch } from '../../core/api.js';
import { aiChatMessagesEl, getAiChatInitData } from './dom.js';
import { scrollAiChatToBottom, scrollAiChatToBottomSmooth } from './scroll.js';
import { formatAiChatMarkdown } from './markdown.js';
import { cancelAiChatTextStream, streamAiChatBubbleText } from './stream.js';
import type { AiChatMessage, AiHistoryApiResponse } from '../../types/api.js';

export interface AiChatBubbleRow extends HTMLDivElement {
  _aiStreamPromise?: Promise<void>;
}

export interface AiChatBubbleOptions {
  animate?: boolean;
  instantScroll?: boolean;
  fadeOnly?: boolean;
  sendIn?: boolean;
  incoming?: boolean;
  deferScroll?: boolean;
  loading?: boolean;
  streamText?: boolean;
}

export function clearAiChatMessagesDom(): void {
  if (!aiChatMessagesEl) return;
  cancelAiChatTextStream();
  aiChatMessagesEl.innerHTML = '';
}

export async function loadAiChatHistoryFromServer(): Promise<AiChatMessage[]> {
  const initData = getAiChatInitData();
  if (!initData) return [];
  const res = await apiFetch('/api/ai/history', {
    method: 'GET',
    cache: 'no-store'
  });
  const data = (await res.json().catch(function () {
    return {};
  })) as AiHistoryApiResponse;
  if (!res.ok || !data.ok || !Array.isArray(data.messages)) return [];
  return data.messages;
}

export function aiChatHasWelcomeBubble(): boolean {
  return !!(aiChatMessagesEl && aiChatMessagesEl.querySelector('[data-ai-welcome="1"]'));
}

export function appendAiChatBubble(
  role: 'user' | 'assistant',
  text: string,
  options?: AiChatBubbleOptions
): AiChatBubbleRow | null {
  if (!aiChatMessagesEl) return null;
  options = options || {};
  const isUser = role === 'user';
  const animateIn = options.animate !== false;
  const instantScroll = !!options.instantScroll;
  const fadeOnly = !!options.fadeOnly;
  const sendIn = !!options.sendIn;
  const incoming = !!options.incoming;
  const deferScroll = !!options.deferScroll;
  const reducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const newOnlyRevealLift = animateIn && !fadeOnly && !reducedMotion && (sendIn || incoming);

  const row = document.createElement('div') as AiChatBubbleRow;
  row.className = isUser ? 'chat-msg-row chat-msg-row-me' : 'chat-msg-row chat-msg-row-them';

  if (!isUser) {
    const avatar = document.createElement('div');
    avatar.className = 'chat-avatar';
    avatar.textContent = 'ИИ';
    row.appendChild(avatar);
  }

  const col = document.createElement('div');
  col.className = 'chat-msg-col';

  const meta = document.createElement('div');
  meta.className = 'chat-msg-meta';
  const nameEl = document.createElement('div');
  nameEl.className = 'chat-msg-name';
  nameEl.textContent = isUser ? 'Вы' : 'Ассистент';
  meta.appendChild(nameEl);
  col.appendChild(meta);

  const bubble = document.createElement('div');
  bubble.className = isUser ? 'chat-bubble chat-bubble-me' : 'chat-bubble chat-bubble-them';
  if (options.loading) {
    bubble.classList.add('chat-bubble--loading');
    bubble.innerHTML = '<span class="ai-thinking-text" aria-live="polite">Думаю</span>';
  } else if (!isUser) {
    const textEl = document.createElement('div');
    textEl.className = 'chat-bubble-text';
    const streamText = !!options.streamText && String(text || '').length > 0;
    if (!streamText) textEl.innerHTML = formatAiChatMarkdown(text);
    bubble.appendChild(textEl);
  } else {
    const userText = document.createElement('div');
    userText.className = 'chat-bubble-text';
    userText.textContent = String(text || '');
    bubble.appendChild(userText);
  }
  col.appendChild(bubble);
  row.appendChild(col);

  if (animateIn && !newOnlyRevealLift) {
    if (sendIn && isUser) row.classList.add('chat-msg-row--send-in');
    else {
      row.classList.add('chat-msg-row--animate-in');
      if (fadeOnly) row.classList.add('chat-msg-row--fade-only');
    }
  }

  aiChatMessagesEl.appendChild(row);

  if (newOnlyRevealLift) {
    try {
      const rowHeight = row.getBoundingClientRect().height || row.offsetHeight || 0;
      const liftPx = Math.max(12, Math.round(rowHeight + 10));
      row.style.transition = 'none';
      row.style.willChange = 'transform, opacity';
      row.style.opacity = '0';
      row.style.transform = 'translateY(' + liftPx + 'px)';
      scrollAiChatToBottomSmooth(520);
      requestAnimationFrame(function () {
        try {
          row.style.transition = 'transform 0.52s cubic-bezier(0.18, 0.95, 0.2, 1), opacity 0.42s ease';
          row.style.transform = 'translateY(0)';
          row.style.opacity = '1';
        } catch (_) {}
      });
      setTimeout(function () {
        try {
          row.style.transition = '';
          row.style.transform = '';
          row.style.opacity = '';
          row.style.willChange = '';
        } catch (_) {}
      }, 620);
    } catch (_) {}
  }

  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      if (animateIn) {
        if (newOnlyRevealLift || deferScroll) return;
        if (instantScroll) scrollAiChatToBottom();
        else scrollAiChatToBottomSmooth();
      } else scrollAiChatToBottom();
    });
  });

  if (!isUser && options.streamText && String(text || '').length > 0) {
    const streamEl = row.querySelector('.chat-bubble-text') as HTMLElement | null;
    const startStream = function (): void {
      row._aiStreamPromise = streamAiChatBubbleText(streamEl, String(text || ''));
    };
    if (newOnlyRevealLift) setTimeout(startStream, 680);
    else requestAnimationFrame(startStream);
  }

  return row;
}
